/**
 * The field, and what everybody is getting.
 *
 * The handicaps used to live only behind the Setup PIN, which made the one
 * question everybody asks — "what's he playing off?" — answerable by exactly
 * one person. That is the wrong shape for a group of twelve who have been
 * arguing about each other's handicaps for years: the argument is the point,
 * and it needs a shared sheet rather than a gatekeeper.
 *
 * Read-only on purpose. Everybody can see the numbers; changing them stays
 * behind the PIN, so the sheet settles arguments without handing anyone the
 * controls.
 */

import { useState } from 'react'
import { inPlayOrder } from '../select'
import type { Tournament } from '../engine/types'
import { courseHandicapFor, findTee, holesFor, playingIndex } from '../engine/handicap'
import { Bar, Empty } from '../ui'

export function Field({ doc, header }: { doc: Tournament; header?: React.ReactNode }) {
  const active = doc.players.filter((p) => p.active)

  /*
   * A course handicap needs a card, so one has to be chosen — and it is chosen
   * here, not fixed. The sheet used to quote the first round's course all week,
   * which is right on Sunday morning and wrong by Monday: the slopes at Bandon
   * differ enough that a man's CH moves two strokes between cards, and this
   * sheet exists to settle exactly that argument. The dropdown offers each
   * course the week actually plays, once, in play order; the first round's
   * stays the opening pick because it is the card everybody is holding when
   * they first open this.
   */
  const played: string[] = []
  for (const r of inPlayOrder(doc.rounds)) {
    if (r.courseId && !played.includes(r.courseId)) played.push(r.courseId)
  }
  const [courseId, setCourseId] = useState(played[0])
  const course = doc.courses.find((c) => c.id === courseId)
  /*
   * Eighteen holes, not the first round's nine.
   *
   * This took `holesFor(course, firstRound.nine)`, and the first round of this
   * week is a FRONT NINE — so every figure on the roster was a nine-hole course
   * handicap presented as "the strokes that tee gives him at Bandon Dunes".
   * Measured against the live roster: Scott P. off 12.1 read CH 7 where his
   * eighteen-hole number is 13. Half of every man's allowance, on the one screen
   * the twelve go to in order to argue about allowances — and worse, halving
   * COMPRESSES the field. Six men, Tyler (4.8) through Thomas (5.9), all printed
   * the same 3 where they truly span 5 to 6. The column exists to be read down,
   * and rounding to nine holes flattened exactly that.
   *
   * A roster figure is not round-specific, so it should not borrow a round's
   * nine. The full card is the comparable number, and it is the one anybody
   * means when they ask what somebody is playing off.
   */
  const holes = course ? holesFor(course, 'full18') : []

  // Lowest playing index first — the order the field is actually in.
  const rows = [...active].sort((a, b) => playingIndex(a) - playingIndex(b))

  return (
    <>
      <Bar
        kicker={course ? `CH over 18 at ${course.name}` : 'The roster'}
        title="The field"
        sub={`${active.length} players`}
      />
      {header}

      {played.length > 1 && (
        <div className="px-4 py-3 border-b border-rule">
          <select
            value={courseId ?? ''}
            onChange={(e) => setCourseId(e.target.value)}
            aria-label="Course for the CH column"
            className="bc-label border border-ink px-2 py-2 bc-tap bg-paper w-full"
          >
            {played.map((id) => (
              <option key={id} value={id}>
                {doc.courses.find((c) => c.id === id)?.name ?? id}
              </option>
            ))}
          </select>
        </div>
      )}

      {rows.length === 0 ? (
        <Empty>Nobody on the roster yet.</Empty>
      ) : (
        <>
          <div className="grid grid-cols-[minmax(0,1fr)_2.75rem_2.75rem_1.75rem] gap-1.5 items-end px-4 pb-1 pt-3 border-b border-rule">
            <span className="bc-label text-mute">Player</span>
            <span className="bc-label text-mute text-center">Index</span>
            <span className="bc-label text-mute text-center">Low</span>
            <span className="bc-label text-mute text-right">CH</span>
          </div>

          <ul className="divide-y divide-rule border-b border-rule">
            {rows.map((p) => {
              const tee = course ? findTee(course, p) : null
              const ch =
                course && tee && holes.length > 0
                  ? courseHandicapFor(playingIndex(p), tee, holes)
                  : null
              const onLow = p.playsOff === 'low' && typeof p.lowIndex === 'number'
              /*
               * The number in play is set in ink and the other in gray. Both
               * are always shown, because the comparison is the whole reason
               * for holding two — a man three shots above his low is exactly
               * what this sheet is for.
               */
              const live = 'bc-num text-sm text-center'
              const idle = 'bc-num text-sm text-center text-dim'
              return (
                <li
                  key={p.id}
                  className="grid grid-cols-[minmax(0,1fr)_2.75rem_2.75rem_1.75rem] gap-1.5 items-center px-4 py-2.5"
                >
                  <span className="min-w-0">
                    <span className="bc-name text-sm truncate block">{p.displayName}</span>
                    <span className="text-[12px] text-mute truncate block">
                      {[tee?.name, onLow ? 'off his low' : null].filter(Boolean).join(' · ')}
                    </span>
                  </span>
                  <span className={onLow ? idle : live}>{p.handicapIndex.toFixed(1)}</span>
                  <span className={onLow ? live : idle}>
                    {typeof p.lowIndex === 'number' ? p.lowIndex.toFixed(1) : '—'}
                  </span>
                  <span className="bc-num text-sm text-right">{ch ?? '—'}</span>
                </li>
              )
            })}
          </ul>

          <p className="px-4 py-4 text-[13px] text-mute leading-snug">
            <b className="bc-name text-ink">Low</b> is his best of the last twelve months.
            Whichever number he plays off is in black, and <b className="bc-name text-ink">CH</b>{' '}
            is what it gives him over eighteen{course ? ` at ${course.name}` : ''}.
          </p>
        </>
      )}
    </>
  )
}
